import { Button } from '@/components/ui/button';
import { 
  Plus, 
  FileText, 
  CreditCard, 
  AlertTriangle,
  Pill,
  Stethoscope,
  Building2,
  UserRound,
  Phone,
  Users,
  Users2,
  Car,
  Wrench,
  PawPrint,
  Wallet,
  Home,
  ShoppingCart,
  Calendar,
  ClipboardList
} from 'lucide-react';
import { useDemo } from '@/contexts/DemoContext';

/**
 * EmptyState - Educational, encouraging empty states
 * 
 * In demo mode, shows educational text about what belongs in this section.
 * In normal mode, shows a simple "no items" message with action button.
 * 
 * Design philosophy: Empty states should educate and encourage, not just inform.
 * 
 * Visual consistency:
 * - Icon: h-8 w-8 in a 64x64 rounded-2xl container
 * - Title: text-lg font-semibold text-slate-700
 * - Description: text-sm text-slate-500, max-w-md
 * - Single CTA button per empty state
 */

// Icon mapping for consistent iconography
const ICON_MAP = {
  documents: FileText,
  passport: CreditCard,
  insurance: CreditCard,
  allergies: AlertTriangle,
  medications: Pill,
  conditions: Stethoscope,
  surgeries: Building2,
  doctors: UserRound,
  emergencyContacts: Phone,
  contacts: Users,
  familyMembers: Users2,
  vehicles: Car,
  maintenance: Wrench,
  pets: PawPrint,
  financial: Wallet,
  household: Home,
  shoppingLists: ShoppingCart,
  importantDates: Calendar,
  default: ClipboardList,
};

const EDUCATIONAL_CONTENT = {
  // Documents
  documents: {
    title: 'Your Important Documents',
    description: 'Store digital copies of passports, driver\'s licenses, birth certificates, and other vital records. Having these accessible can save hours during emergencies.',
    examples: ['Passports & IDs', 'Birth certificates', 'Insurance cards', 'Medical records'],
  },
  passport: {
    title: 'Your Passport Would Appear Here',
    description: 'Keep a digital copy of your passport for emergencies. If lost abroad, this can speed up the replacement process significantly.',
    examples: ['Photo page', 'Visa stamps', 'Expiration date'],
  },
  insurance: {
    title: 'Your Insurance Information',
    description: 'Store all your insurance cards and policy details. In an emergency room, having instant access to your coverage info can reduce stress.',
    examples: ['Health insurance', 'Dental & vision', 'Auto insurance', 'Home/renters insurance'],
  },
  
  // Medical
  allergies: {
    title: 'Allergies & Sensitivities',
    description: 'Document allergies to medications, foods, and environmental triggers. This information could save your life in an emergency.',
    examples: ['Drug allergies (e.g., Penicillin)', 'Food allergies', 'Latex sensitivity', 'Seasonal allergies'],
  },
  medications: {
    title: 'Current Medications',
    description: 'Keep track of all prescriptions, dosages, and schedules. Share this with any new doctor or pharmacist instantly.',
    examples: ['Prescription medications', 'Vitamins & supplements', 'OTC medications', 'Emergency medications (EpiPen)'],
  },
  conditions: {
    title: 'Medical Conditions',
    description: 'Document ongoing health conditions and their management plans. First responders need this information quickly.',
    examples: ['Chronic conditions', 'Past diagnoses', 'Ongoing treatments', 'Management notes'],
  },
  surgeries: {
    title: 'Surgical History',
    description: 'Record past surgeries and procedures. This history helps doctors make better decisions about your care.',
    examples: ['Major surgeries', 'Minor procedures', 'Implants or devices', 'Recovery notes'],
  },
  doctors: {
    title: 'Your Healthcare Team',
    description: 'Keep contact info for all your doctors and specialists in one place. Never scramble to find a phone number again.',
    examples: ['Primary care physician', 'Specialists', 'Dentist & eye doctor', 'Therapists'],
  },
  
  // Contacts
  emergencyContacts: {
    title: 'Emergency Contacts',
    description: 'List the people who should be called in an emergency. Include relationships and multiple phone numbers.',
    examples: ['Spouse/partner', 'Parents or children', 'Close friends', 'Work contacts'],
  },
  contacts: {
    title: 'Important Contacts',
    description: 'Store contact information for family, friends, and service providers you rely on.',
    examples: ['Family members', 'Neighbors', 'Service providers', 'Schools/workplaces'],
  },
  
  // Family
  familyMembers: {
    title: 'Family Members',
    description: 'Add family members to create individual profiles for each person. Track everyone\'s medical info in one place.',
    examples: ['Spouse', 'Children', 'Parents', 'Dependents'],
  },
  
  // Vehicles
  vehicles: {
    title: 'Your Vehicles',
    description: 'Store vehicle details, registration info, and maintenance history. Never forget when that oil change is due.',
    examples: ['Car details & VIN', 'Registration & insurance', 'Maintenance schedule', 'Warranty info'],
  },
  maintenance: {
    title: 'Maintenance Records',
    description: 'Track all vehicle maintenance and repairs. This history increases resale value and catches issues early.',
    examples: ['Oil changes', 'Tire rotations', 'Major repairs', 'Inspections'],
  },
  
  // Pets
  pets: {
    title: 'Your Pets',
    description: 'Store pet info including vet contacts, medications, and emergency care instructions. Peace of mind when you travel.',
    examples: ['Vet information', 'Vaccinations', 'Medications', 'Dietary needs'],
  },
  
  // Financial
  financial: {
    title: 'Financial Overview',
    description: 'Keep track of accounts, policies, and important financial contacts. Essential for estate planning.',
    examples: ['Bank accounts', 'Investment accounts', 'Insurance policies', 'Financial advisors'],
  },
  
  // Household
  household: {
    title: 'Household Information',
    description: 'Store WiFi passwords, utility contacts, and home maintenance details. Perfect for housesitters.',
    examples: ['WiFi & alarm codes', 'Utility contacts', 'Maintenance schedules', 'Emergency shutoffs'],
  },
  
  // Shopping
  shoppingLists: {
    title: 'Shopping Lists',
    description: 'Create and share shopping lists with family members. Never forget the milk again.',
    examples: ['Grocery lists', 'Back-to-school supplies', 'Trip packing lists', 'Gift ideas'],
  },
  
  // Dates
  importantDates: {
    title: 'Important Dates',
    description: 'Track birthdays, anniversaries, appointments, and renewals. Get gentle reminders before they sneak up.',
    examples: ['Birthdays', 'Anniversaries', 'Appointments', 'Membership renewals'],
  },
  
  // Default
  default: {
    title: 'Nothing Here Yet',
    description: 'Add your first item to get started.',
    examples: [],
  },
};

const EmptyState = ({ 
  type = 'default',
  onAction,
  actionLabel = 'Add First Item',
  customTitle,
  customDescription,
  showExamples = true,
  className = ''
}) => {
  const { isDemoMode } = useDemo();
  
  const content = EDUCATIONAL_CONTENT[type] || EDUCATIONAL_CONTENT.default;
  const title = customTitle || content.title;
  const description = customDescription || content.description;
  
  // Get the appropriate icon component
  const IconComponent = ICON_MAP[type] || ICON_MAP.default;
  
  return (
    <div className={`text-center py-12 px-4 ${className}`} data-testid={`empty-state-${type}`}>
      {/* Icon container - consistent 64x64 with rounded-2xl */}
      <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
        <IconComponent className="h-8 w-8 text-slate-400" />
      </div>
      
      <h3 className="text-lg font-semibold text-slate-700 mb-2">{title}</h3>
      <p className="text-sm text-slate-500 max-w-md mx-auto mb-4 leading-relaxed">
        {description}
      </p>
      
      {/* Example items - educational */}
      {showExamples && content.examples && content.examples.length > 0 && (
        <div className="flex flex-wrap justify-center gap-2 mb-6 max-w-sm mx-auto">
          {content.examples.map((example, i) => (
            <span 
              key={i}
              className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full"
            >
              {example}
            </span>
          ))}
        </div>
      )}
      
      {/* Single CTA - consistent styling */}
      {onAction && (
        <Button 
          onClick={onAction} 
          className="bg-slate-800 hover:bg-slate-900 text-white"
          data-testid={`empty-state-${type}-action`}
        >
          <Plus className="h-4 w-4 mr-2" />
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

export default EmptyState;
